import { useEffect, useId } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { createNadDebug } from '../../utils/debugLog'
import { META_PRIVACY_URL, META_TERMS_URL } from '../../constants/legal'

// NAD feature tag for this compliance surface (RQ16043652).
const log = createNadDebug('dw:auth:terms-ack')

interface TermsAcknowledgmentProps {
  /** Which account-creation action this instance sits above (for NAD logs). */
  context: 'email-signup' | 'oauth'
  /** Current tick state of the consent checkbox. */
  accepted: boolean
  /** Called with the new tick state. The owner gates its action on `accepted`. */
  onAcceptedChange: (accepted: boolean) => void
  className?: string
}

/**
 * Clickwrap consent shown immediately above an account-creation action (email
 * sign-up submit button, or the OAuth buttons). Satisfies RQ16043652: names the
 * Terms, links them, states what + how ("By continuing, you agree…"), and is
 * placed directly above the action so it stays co-visible on every viewport.
 *
 * Consent is *explicit*, not implied: the checkbox starts unticked and the
 * owning surface keeps its account-creation action disabled until it is ticked.
 * `accepted` / `onAcceptedChange` are required rather than optional so a new
 * call site cannot silently ship an ungated variant of this copy.
 */
export default function TermsAcknowledgment({
  context,
  accepted,
  onAcceptedChange,
  className,
}: TermsAcknowledgmentProps) {
  const { t } = useTranslation('auth')
  const checkboxId = useId()
  const textId = useId()

  useEffect(() => {
    log({ event: 'terms_ack_rendered', context })
  }, [context])

  return (
    <div
      data-testid="terms-acknowledgment"
      data-ack-context={context}
      className={`flex items-start gap-2 ${className ?? ''}`}
    >
      <input
        type="checkbox"
        id={checkboxId}
        data-testid="terms-accept-checkbox"
        checked={accepted}
        // Announce the box as required. The owning surface already disables its
        // action while unticked, so native constraint validation can never
        // actually fire here — this is purely so assistive technology reports
        // the consent as mandatory rather than optional.
        required
        // The visible copy carries the links, so it can't be wrapped in a
        // <label> (clicking a link would toggle the box). Associate it as the
        // description instead, and give the input its own short a11y name.
        aria-describedby={textId}
        onChange={(event) => {
          const next = event.target.checked
          log({ event: 'terms_checkbox_toggled', context, accepted: next })
          onAcceptedChange(next)
        }}
        className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded border-white/30 bg-white/[0.06] accent-[#8dd5d8]"
      />
      <label htmlFor={checkboxId} className="sr-only">
        {t('termsCheckboxLabel')}
      </label>
      <p id={textId} className="text-xs font-medium text-text-muted">
        <Trans
          i18nKey="termsText"
          t={t}
          components={{
            termsLink: (
              <a
                href={META_TERMS_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="terms-link"
                className="text-primary hover:underline"
                onClick={() => log({ event: 'meta_terms_link_click', context, url: META_TERMS_URL })}
              />
            ),
            privacyLink: (
              <a
                href={META_PRIVACY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="privacy-link"
                className="text-primary hover:underline"
                onClick={() => log({ event: 'privacy_link_click', context, url: META_PRIVACY_URL })}
              />
            ),
          }}
        />
      </p>
    </div>
  )
}
